import {
  BACKEND_QUERY_PARAM,
  BACKEND_STORAGE_KEY,
  type BackendKey,
  activeBackend,
  allowsRuntimeBackendSwitch,
  isBackendKey
} from './env'

/** Module-level guard. `switchBackend` ends in a hard reload, so a successful
 * call should never run twice — but React StrictMode (dev) double-fires
 * effects, and double-clicks on the pill could otherwise re-enter. The flag
 * is reset only on early-return paths (the call did nothing destructive); the
 * happy path leaves it `true` because the reload will tear down the page. */
let switchInFlight = false

/**
 * Switches the active backend. Clears localStorage **and** sessionStorage,
 * then reloads at the same URL with `?backend=<next>` set, so a shared/copied
 * URL during preview builds always carries the active backend. Session storage
 * must go too — the org-hierarchy cloak restore key lives there, and leaving
 * it would replay a prod-US cloak path after switching to prod-EU on the same
 * preview origin. IndexedDB cleanup is handled post-reload by
 * `useIndexedDbCleanup` once the new page mounts unauthenticated.
 *
 * Returns `true` once it has committed to navigating (the page is about to
 * reload, so the caller should leave its UI in a "switching..." state).
 * Returns `false` if it bailed without doing anything destructive (callers
 * should reset their UI so the user isn't stuck staring at a permanent
 * spinner).
 *
 * No-op when the current build profile does not allow runtime switching.
 */
export function switchBackend(next: BackendKey): boolean {
  if (!allowsRuntimeBackendSwitch) return false
  if (typeof window === 'undefined') return false
  if (!isBackendKey(next)) return false
  // Idempotency guard — without this, picking the already-active backend (via
  // the `B <key>` chord or the `BackendSwitcher` chip) still runs
  // `localStorage.clear()` + a hard reload, silently logging the user out.
  if (next === activeBackend) return false
  if (switchInFlight) return false
  switchInFlight = true

  const url = new URL(window.location.href)
  url.searchParams.set(BACKEND_QUERY_PARAM, next)
  const destination = url.toString()

  try {
    window.localStorage.clear()
    window.localStorage.setItem(BACKEND_STORAGE_KEY, next)
    window.sessionStorage.clear()
  } catch {
    switchInFlight = false
    return false
  }
  window.location.replace(destination)
  return true
}

/**
 * Reads the `?backend=<key>` query param. Returns the value only if it is a
 * valid backend key, otherwise null. Always returns null outside `preview`
 * builds.
 */
export function readBackendQueryParam(): BackendKey | null {
  if (!allowsRuntimeBackendSwitch) return null
  if (typeof window === 'undefined') return null

  try {
    const value = new URLSearchParams(window.location.search).get(BACKEND_QUERY_PARAM)
    return isBackendKey(value) ? value : null
  } catch {
    return null
  }
}
